import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { RouterModule } from "@angular/router";
import { AngularSvgIconModule } from "angular-svg-icon";

import { N02_COPY } from "../../services/onboarding-lessons/n02-how-nova-works.service";

/**
 * The Nova full-panel first-use surface (N02 "How Nova works", FLIP-389), built from Figma
 * "Onboarding Component - N01 Nova Overlay" (node 33:8935): a "Getting started" eyebrow, the four
 * sections in one continuous scrollable view (separated by glacier rules, no Next / Continue /
 * Got it - they are not steps), and the Help Center line pinned at the foot.
 *
 * It replaces the chat area of the Nova sheet, and only that: NovaCompanionChatComponent keeps
 * Nova's header - minus Voice Chat - and its close, which on this step closes the panel and
 * reveals the chat rather than closing Nova. The panel therefore has no close of its own. It is
 * plain component state: it never goes through the chat service, the prompt system or the
 * conversation history. It does not decide when it shows or what closing means: that is
 * N02HowNovaWorksService.
 */
@Component({
  selector: "app-nova-first-use-panel",
  standalone: true,
  imports: [CommonModule, RouterModule, AngularSvgIconModule],
  templateUrl: "./nova-first-use-panel.component.html",
  styleUrls: ["./nova-first-use-panel.component.scss"],
})
export class NovaFirstUsePanelComponent implements OnInit, OnDestroy {
  readonly copy = N02_COPY;

  /** Following the Help Center link leaves the panel - it counts as closing it. */
  @Output() closed = new EventEmitter<void>();

  constructor(private host: ElementRef<HTMLElement>) {}

  /** The panel replaces the chat, so focus moves to it for keyboard and screen-reader users. */
  ngOnInit(): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(".nova-first-use__heading")?.focus());
    this.trackHeaderHeight();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private resizeObserver?: ResizeObserver;

  /**
   * The design's gradient is laid over the whole card, but the panel starts below Nova's header, so
   * the stylesheet needs to know how tall that header is. Measured as the panel's distance from the
   * top of the sheet, and re-measured when the sheet resizes (it is viewport-sized).
   */
  private trackHeaderHeight(): void {
    const panel = this.host.nativeElement;
    const card = panel.closest<HTMLElement>(".base-modal");
    if (!card) {
      return;
    }
    const measure = () => {
      const offset = panel.getBoundingClientRect().top - card.getBoundingClientRect().top;
      panel.style.setProperty("--nova-first-use-head-height", `${Math.max(0, Math.round(offset))}px`);
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(measure);
      this.resizeObserver.observe(card);
    }
  }
}
